import { useEffect, useState } from 'react';
import {
  DEFAULT_INTEREST_LABELS,
  defaultInterestTagOverrides,
  defaultInterestEventLevels,
  type InterestEventLevel,
  type InterestTagConfig,
  type InterestTagOverride,
  type PromoEventType,
  type PromoInterestConfigUpsertRequest,
  type PromoInterestConfigUpsertResponse,
  type PromoInterestKey,
} from '@kawai/shared';
import {
  INTEREST_KEY_OPTIONS,
  INTEREST_KEY_LABELS,
  EVENT_TYPE_LABELS,
} from '../lib/segment.js';
import type { SelectOption } from '../lib/hooks/useSegmentMasterOptions.js';
import { callPromoInterestConfigUpsert } from '../lib/api/interestConfigActions.js';
import {
  InterestEngineSection,
  buildEngineForm,
  engineOverrideFromForm,
  GroupHeader,
  type EngineForm,
  type GroupMode,
} from './InterestEngineSection.js';

interface InterestTagConfigModalProps {
  config: InterestTagConfig | null;
  menuOptions: SelectOption[];
  categoryOptions: SelectOption[];
  onClose: () => void;
  /** 保存 + 再集計成功時。親は invalidateQueries（interestTagConfig / allUsers）する。 */
  onSaved: (res: PromoInterestConfigUpsertResponse) => void;
}

/** 行動種別 → 確度の選択肢（select 用 / null=付与しない / estimated は score 3 段階）。 */
type EventLevelOption =
  | 'none'
  | 'estimated-low'
  | 'estimated-mid'
  | 'estimated-high'
  | 'self_declared'
  | 'confirmed';

/**
 * option 値 → 表示ラベルの真実源（select の選択肢と「既定に追従」時の要約表示の両方）。
 * `Record<EventLevelOption, string>` なので選択肢の追加漏れは型エラーになる。
 */
const LEVEL_LABEL_BY_OPTION: Readonly<Record<EventLevelOption, string>> = {
  none: '付与しない',
  'estimated-low': '推定（弱）',
  'estimated-mid': '推定（中）',
  'estimated-high': '推定（強）',
  self_declared: '申告',
  confirmed: '確定',
};

/** select の選択肢（表示順 = 上の定義順 / segment.ts の SEGMENT_EVENT_TYPE_OPTIONS と同イディオム）。 */
const LEVEL_OPTIONS: ReadonlyArray<{ value: EventLevelOption; label: string }> = (
  Object.keys(LEVEL_LABEL_BY_OPTION) as EventLevelOption[]
).map((value) => ({ value, label: LEVEL_LABEL_BY_OPTION[value] }));

/**
 * 興味タグ設定で編集する行動種別（message_sent は配信記録でユーザー行動でないため除外）。
 * survey_answer は記録経路（M-78）実装済みのため編集対象（M-73 仕上げ）。
 */
const EDITABLE_EVENT_TYPES: readonly PromoEventType[] = [
  'reservation_completed',
  'richmenu_tap',
  'liff_link_tap',
  'liff_visit',
  'booking_page_reach',
  'survey_answer',
  'coupon_claim',
  'coupon_redeem',
];

/**
 * 既定の行動種別 → 確度（非破壊コピー）。「既定に追従」時の実効値表示と、
 * 保存済み値が既定と同値か（= 明示 override ではない）の判定に使う。
 */
const DEFAULT_EVENT_LEVELS_FALLBACK = defaultInterestEventLevels();

function toEventLevelOption(v: InterestEventLevel | null | undefined): EventLevelOption {
  if (v == null) return 'none';
  if (v.level === 'estimated') return `estimated-${v.score ?? 'mid'}` as EventLevelOption;
  return v.level; // 'self_declared' | 'confirmed'
}

function fromEventLevelOption(o: EventLevelOption): InterestEventLevel | null {
  if (o === 'none') return null;
  if (o.startsWith('estimated-')) {
    const score = o.slice('estimated-'.length) as 'low' | 'mid' | 'high';
    return { level: 'estimated', score };
  }
  return { level: o as 'self_declared' | 'confirmed' };
}

interface TagForm {
  label: string;
  menuIds: string[];
  categoryIds: string[];
}
/** M-81: 行動種別 1 件分の確度フォーム（差分保存 — custom のみ payload に載る）。 */
export interface EventLevelForm {
  mode: GroupMode;
  value: EventLevelOption;
}
interface FormState {
  tags: Record<PromoInterestKey, TagForm>;
  /** M-81: 種別ごとに「既定に追従 / 上書き」を持つ（差分保存）。 */
  eventLevels: Record<string, EventLevelForm>;
  /**
   * M-81: UI の編集対象外（EDITABLE_EVENT_TYPES 外 = message_sent / cycle_reset）だが
   * config に保存済みの上書き。`tx.set` 全置換のもとで UI 保存が黙って消さないよう、
   * そのまま保存 payload へ載せ替える（backfill スクリプトの full 再送経路が作り得る）。
   */
  eventLevelsPassthrough: Partial<Record<PromoEventType, InterestEventLevel | null>>;
  /** M-74: 商材ごとの engine 設定（差分保存 — custom グループのみ payload に載る）。 */
  engines: Record<PromoInterestKey, EngineForm>;
}

function buildInitialForm(config: InterestTagConfig | null): FormState {
  // config.tags が定義済みなら config が真実源（未設定キーは空）。未保存なら既定をプリロード。
  const baseTags = config?.tags ?? defaultInterestTagOverrides();
  const tags = {} as Record<PromoInterestKey, TagForm>;
  for (const { value: key } of INTEREST_KEY_OPTIONS) {
    const o = baseTags[key];
    tags[key] = {
      label: o?.label ?? DEFAULT_INTEREST_LABELS[key],
      menuIds: o?.menuIds ? [...o.menuIds] : [],
      categoryIds: o?.categoryIds ? [...o.categoryIds] : [],
    };
  }
  // M-81: eventLevels も差分保存 — 保存済み値が**現行の既定と異なる**種別だけ custom で
  // 復元し、それ以外は「既定に追従」。M-62〜M-80 のモーダルは全種別を明示値として焼き込んで
  // いたため、既定と同値の焼き込みはここで追従扱いに落とし、次の保存で config から消える
  // （実効値は不変 = 表示・タグ判定に変化なし / issue M-81 の migration 論点への回答）。
  const eventLevels: Record<string, EventLevelForm> = {};
  for (const t of EDITABLE_EVENT_TYPES) {
    const saved = config?.eventLevels?.[t];
    const value = toEventLevelOption(
      saved !== undefined ? saved : DEFAULT_EVENT_LEVELS_FALLBACK[t],
    );
    const isExplicit =
      saved !== undefined &&
      value !== toEventLevelOption(DEFAULT_EVENT_LEVELS_FALLBACK[t]);
    eventLevels[t] = { mode: isExplicit ? 'custom' : 'default', value };
  }
  // 編集対象外キー（message_sent / cycle_reset）の保存済み上書きは素通しで維持する。
  // 未知の種別は載せない — Callable の eventLevels スキーマは strict で、直書きされた
  // 想定外キーを送り返すと保存自体が invalid-argument で落ちるため。
  const eventLevelsPassthrough: Partial<
    Record<PromoEventType, InterestEventLevel | null>
  > = {};
  for (const [k, v] of Object.entries(config?.eventLevels ?? {}) as [
    PromoEventType,
    InterestEventLevel | null | undefined,
  ][]) {
    if (v === undefined || EDITABLE_EVENT_TYPES.includes(k)) continue;
    if (!(k in DEFAULT_EVENT_LEVELS_FALLBACK)) continue;
    eventLevelsPassthrough[k] = v;
  }
  // M-74: engines は差分保存 — 保存済み上書きのあるグループのみ custom で復元し、
  // それ以外は「既定に追従」（全量プリロードする tags とは意図的に非対称）。
  const engines = {} as Record<PromoInterestKey, EngineForm>;
  for (const { value: key } of INTEREST_KEY_OPTIONS) {
    engines[key] = buildEngineForm(key, config);
  }
  return { tags, eventLevels, eventLevelsPassthrough, engines };
}

/**
 * M-81: フォームの eventLevels から差分 payload を組み立てる（custom の種別のみ + 編集対象外の
 * 保存済み上書き）。全 default なら空 `{}` を返す — Callable へ `eventLevels: {}` として送り
 * 「全て既定に追従 = 上書きなし」を明示する（キー欠落 = 既存保持 と区別するため handleSave は
 * 必ず本オブジェクトを送る）。null（付与しない）も custom の明示値として保持する。
 */
export function eventLevelOverridesFromForm(
  eventLevels: Record<string, EventLevelForm>,
  passthrough: Partial<Record<PromoEventType, InterestEventLevel | null>> = {},
): NonNullable<PromoInterestConfigUpsertRequest['eventLevels']> {
  const out: NonNullable<PromoInterestConfigUpsertRequest['eventLevels']> = {
    ...passthrough,
  };
  for (const type of EDITABLE_EVENT_TYPES) {
    const ef = eventLevels[type];
    if (ef && ef.mode === 'custom') {
      out[type] = fromEventLevelOption(ef.value);
    }
  }
  return out;
}

function toggle(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

const inputClass =
  'w-full rounded-lg bg-surface-2 px-3.5 py-2 text-body text-ink focus:outline-hidden focus:ring-2 focus:ring-brand';

/**
 * M-62 (promo): 興味タグ設定モーダル。
 *
 * 興味タグ（業態別）の「表示名」と「タグ付け条件」（メニュー/カテゴリ対応 + 行動種別→確度）を編集する。
 * 保存すると Callable が config を保存し、過去ログ（promo_events + bookings）から全顧客の興味タグを
 * 全件再集計する（結果の更新/クリア人数を表示）。骨格は CampaignPreviewModal を踏襲（背景クリック /
 * Escape で閉じる）。
 */
export function InterestTagConfigModal({
  config,
  menuOptions,
  categoryOptions,
  onClose,
  onSaved,
}: InterestTagConfigModalProps) {
  const [form, setForm] = useState<FormState>(() => buildInitialForm(config));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<PromoInterestConfigUpsertResponse | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const updateTag = (key: PromoInterestKey, patch: Partial<TagForm>): void =>
    setForm((p) => ({
      ...p,
      tags: { ...p.tags, [key]: { ...p.tags[key], ...patch } },
    }));
  const updateEvent = (type: string, next: EventLevelForm): void =>
    setForm((p) => ({ ...p, eventLevels: { ...p.eventLevels, [type]: next } }));
  const updateEngine = (key: PromoInterestKey, next: EngineForm): void =>
    setForm((p) => ({ ...p, engines: { ...p.engines, [key]: next } }));

  const handleSave = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const tags: NonNullable<PromoInterestConfigUpsertRequest['tags']> = {};
      for (const { value: key } of INTEREST_KEY_OPTIONS) {
        const f = form.tags[key];
        const o: InterestTagOverride = {};
        const label = f.label.trim();
        if (label) o.label = label;
        if (f.menuIds.length > 0) o.menuIds = f.menuIds;
        if (f.categoryIds.length > 0) o.categoryIds = f.categoryIds;
        tags[key] = o;
      }
      // M-81: eventLevels も差分のみ（custom の種別だけ）。全 default なら `{}` を送り
      // 「上書きなし」を明示する（欠落 = 既存保持と区別）。null override も保持される。
      const eventLevels = eventLevelOverridesFromForm(
        form.eventLevels,
        form.eventLevelsPassthrough,
      );
      // M-74: engines は差分のみ（custom グループの明示値だけ）。全キー既定なら
      // `engines: {}` を送り「上書きなし」を明示する（欠落 = 既存保持と区別）。
      const engines: NonNullable<PromoInterestConfigUpsertRequest['engines']> = {};
      for (const { value: key } of INTEREST_KEY_OPTIONS) {
        const r = engineOverrideFromForm(key, form.engines[key]);
        if (r.error) {
          setErrorMsg(`${INTEREST_KEY_LABELS[key]} のエンジン設定: ${r.error}`);
          setSubmitting(false);
          return;
        }
        if (r.override) engines[key] = r.override;
      }
      const res = await callPromoInterestConfigUpsert({ tags, eventLevels, engines });
      setResult(res);
      onSaved(res);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="interest-config-title"
        className="flex max-h-[90vh] w-[min(560px,96vw)] flex-col rounded-xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-[30px] pb-3 pt-[26px]">
          <h2 id="interest-config-title" className="text-h-card font-semibold text-ink">
            興味タグ設定
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="rounded-lg px-3.5 py-1.5 text-label text-ink-2 hover:bg-surface-2"
          >
            閉じる ✕
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-[30px] py-4">
          {result ? (
            <div
              role="status"
              className="rounded-lg bg-success-tint px-[18px] py-4 text-body text-success-ink"
            >
              <p className="font-semibold">設定を保存し、過去ログから再集計しました。</p>
              <p className="mt-1">
                タグ / ステージ更新: {result.usersUpdated} 人 / クリア:{' '}
                {result.usersCleared} 人
              </p>
              <p className="mt-1">
                再集計対象:{' '}
                {result.recomputedKeys === 'all' || result.recomputedKeys === undefined
                  ? '全商材'
                  : result.recomputedKeys.map((k) => INTEREST_KEY_LABELS[k]).join(' / ')}
              </p>
            </div>
          ) : (
            <>
              <p className="rounded-lg bg-brand-tint px-[18px] py-3 text-label text-ink-2">
                保存すると、過去の行動ログ・予約から全顧客の興味タグとステージを新しい条件で再集計します。
                条件から外れたタグは削除されます。
              </p>

              {/* 業態ごとの表示名・タグ付け条件 */}
              <section className="space-y-3">
                <h3 className="text-label font-medium text-ink-2">
                  集計するタグ名・タグ付け条件（メニュー / カテゴリ）
                </h3>
                {INTEREST_KEY_OPTIONS.map(({ value: key }) => {
                  const f = form.tags[key];
                  const menuCount = f.menuIds.length;
                  const catCount = f.categoryIds.length;
                  return (
                    <div
                      key={key}
                      className="space-y-2.5 rounded-[10px] bg-surface-2 px-[22px] py-5"
                    >
                      <label className="block">
                        <span className="mb-1 block text-label text-ink-3">
                          タグ名（既定: {DEFAULT_INTEREST_LABELS[key]}）
                        </span>
                        <input
                          type="text"
                          className={inputClass}
                          value={f.label}
                          maxLength={40}
                          onChange={(e) => updateTag(key, { label: e.target.value })}
                        />
                      </label>

                      <details className="rounded-lg bg-white">
                        <summary className="cursor-pointer px-3 py-1.5 text-label text-ink-2">
                          対象メニュー（{menuCount} 件選択中）
                        </summary>
                        <div className="max-h-40 space-y-1 overflow-y-auto px-3 py-2">
                          {menuOptions.length === 0 ? (
                            <p className="text-label text-ink-3">
                              メニューを取得できませんでした。
                            </p>
                          ) : (
                            menuOptions.map((o) => (
                              <label
                                key={o.value}
                                className="flex items-center gap-2 text-label text-ink-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={f.menuIds.includes(o.value)}
                                  onChange={() =>
                                    updateTag(key, {
                                      menuIds: toggle(f.menuIds, o.value),
                                    })
                                  }
                                />
                                {o.label}
                              </label>
                            ))
                          )}
                        </div>
                      </details>

                      <details className="rounded-lg bg-white">
                        <summary className="cursor-pointer px-3 py-1.5 text-label text-ink-2">
                          対象カテゴリ（{catCount} 件選択中）
                        </summary>
                        <div className="max-h-40 space-y-1 overflow-y-auto px-3 py-2">
                          {categoryOptions.length === 0 ? (
                            <p className="text-label text-ink-3">
                              カテゴリを取得できませんでした。
                            </p>
                          ) : (
                            categoryOptions.map((o) => (
                              <label
                                key={o.value}
                                className="flex items-center gap-2 text-label text-ink-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={f.categoryIds.includes(o.value)}
                                  onChange={() =>
                                    updateTag(key, {
                                      categoryIds: toggle(f.categoryIds, o.value),
                                    })
                                  }
                                />
                                {o.label}
                              </label>
                            ))
                          )}
                        </div>
                      </details>

                      {/* M-74: 商材別 engine 設定（差分保存） */}
                      <InterestEngineSection
                        interestKey={key}
                        form={form.engines[key]}
                        onChange={(next) => updateEngine(key, next)}
                      />
                    </div>
                  );
                })}
              </section>

              {/* 行動種別 → 確度（M-81: 差分保存 — 種別ごとに既定追従/上書き） */}
              <section className="space-y-2">
                <h3 className="text-label font-medium text-ink-2">
                  行動種別ごとの確度（どの行動でどの強さのタグを付けるか）
                </h3>
                <p className="text-label text-ink-3">
                  「上書き」しない種別は既定値に追従します（既定値が改善されると自動で反映）。
                </p>
                {EDITABLE_EVENT_TYPES.map((type) => {
                  const ef = form.eventLevels[type];
                  const mode = ef?.mode ?? 'default';
                  const defaultSummary =
                    LEVEL_LABEL_BY_OPTION[
                      toEventLevelOption(DEFAULT_EVENT_LEVELS_FALLBACK[type])
                    ];
                  return (
                    <div
                      key={type}
                      data-testid={`event-level-${type}`}
                      className="space-y-1"
                    >
                      <GroupHeader
                        title={EVENT_TYPE_LABELS[type]}
                        mode={mode}
                        defaultSummary={defaultSummary}
                        onToggle={(custom) =>
                          updateEvent(type, {
                            mode: custom ? 'custom' : 'default',
                            value: ef?.value ?? 'none',
                          })
                        }
                      />
                      {mode === 'custom' && (
                        <div className="flex justify-end">
                          <select
                            aria-label={`${EVENT_TYPE_LABELS[type]}の確度`}
                            className="rounded-lg bg-surface-2 px-3 py-1.5 text-label text-ink focus:outline-hidden focus:ring-2 focus:ring-brand"
                            value={ef?.value ?? 'none'}
                            onChange={(e) =>
                              updateEvent(type, {
                                mode: 'custom',
                                value: e.target.value as EventLevelOption,
                              })
                            }
                          >
                            {LEVEL_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>

              {errorMsg && (
                <p role="alert" className="text-body text-danger-ink">
                  {errorMsg}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2.5 px-[30px] pb-[26px] pt-3">
          {result ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-brand px-5 py-2 text-body font-medium text-white hover:opacity-90"
            >
              閉じる
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-surface-2 px-4 py-2 text-body font-medium text-ink hover:bg-surface-3"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={submitting}
                className="rounded-lg bg-brand px-5 py-2 text-body font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? '保存・再集計中…' : '保存して再集計'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
